import React, { useCallback } from 'react';
import { FlatList } from 'react-native';

import { Box } from '@/components/lib/components';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { useSubscribeEventSgpOddsUpdate } from '@/feature/betslip-sbk/hooks/websocket/use-subscribe-event-sgp-odds-update';
import {
    disabledSelectionIdsSelector,
    enabledSelectionIdsSelector,
} from '@/feature/betslip-sbk/utils/betslip-selectors';
import { isComboSelectionEnabled } from '@/feature/betslip-sbk/utils/betslip-utils';
import { useShallow } from 'zustand/react/shallow';

import { DisabledSelectionsHeader } from './DisabledSelectionsHeader';
import { ParlayMatchupHeader } from './ParlayMatchupHeader';
import { ParlaySelection } from './ParlaySelection';

type ParlayEventGroupProps = {
    eventId: string;
};

const ItemSeparator = () => <Box height={8} />;

export const ParlayEventGroup = ({ eventId }: ParlayEventGroupProps) => {
    const enabledSelectionIds = useSbkBetSlipStore(useShallow(enabledSelectionIdsSelector(eventId)));
    const disabledSelectionIds = useSbkBetSlipStore(useShallow(disabledSelectionIdsSelector(eventId)));

    const isValidSgp = useSbkBetSlipStore(state => {
        const activeSelectionCount = Object.values(state.selections).filter(selection =>
            isComboSelectionEnabled(selection, state)
        ).length;
        return !state.sgpEventDisabled[eventId] && activeSelectionCount > 1;
    });
    useSubscribeEventSgpOddsUpdate(eventId, isValidSgp);

    const keyExtractor = useCallback((id: string) => `selection${id}-${eventId}`, [eventId]);

    const renderItem = useCallback(({ item }: { item: string }) => {
        return <ParlaySelection selectionId={item} />;
    }, []);

    const renderDisabledSelectionsHeader = useCallback(() => <DisabledSelectionsHeader eventId={eventId} />, [eventId]);

    const hasDisabledSelections = disabledSelectionIds.length > 0;

    return (
        <Box gap="s8">
            <ParlayMatchupHeader eventId={eventId} />
            <FlatList
                keyExtractor={keyExtractor}
                scrollEnabled={false}
                data={enabledSelectionIds}
                renderItem={renderItem}
                ItemSeparatorComponent={ItemSeparator}
            />
            {hasDisabledSelections ? (
                <FlatList
                    ListHeaderComponent={renderDisabledSelectionsHeader}
                    keyExtractor={keyExtractor}
                    scrollEnabled={false}
                    data={disabledSelectionIds}
                    renderItem={renderItem}
                    ItemSeparatorComponent={ItemSeparator}
                />
            ) : null}
        </Box>
    );
};
